import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, CircularProgress, Alert,
  TextField, MenuItem, FormControl, InputLabel, Select, InputAdornment,
  Collapse, Pagination, styled, Dialog, DialogTitle, DialogContent, 
  DialogActions, DialogContentText, Snackbar, Divider, Card, CardContent,
  List, ListItem, ListItemText, ListItemSecondaryAction, Avatar, Badge,
  Autocomplete
} from '@mui/material';
import {
  Add, Edit, Delete, Visibility, Search, FilterList, ExpandMore, 
  ExpandLess, Refresh, ArrowDropDown, PersonAdd, PersonRemove, 
  Assignment, AssignmentTurnedIn, Warning, CheckCircle, LockReset
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import useFetchObjects from '../../api/useFetchObjects';
import useDelete from '../../api/useDelete';
import { API_BASE_URL } from '../../config';
import { RTLTextField, RTLFormControl, RTLAutocomplete } from '../../styles/rtlComponents';
import useRTL from '../../hooks/useRTL';

// Using centralized RTL components from styles/rtlComponents.js

const RTLTableCell = styled(TableCell)(({ isrtl, align }) => ({
  ...(isrtl === 'true' && { textAlign: align || 'right', direction: 'rtl' })
}));

const UserList = () => {
  const { t, i18n } = useTranslation(['users', 'common']);
  const { user } = useAuth();
  const { isRTL, getRTLProps } = useRTL();

  // State
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [rowPerPage, setRowPerPage] = useState(10);
  
  // Assignment dialog state
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedLimitedAdmin, setSelectedLimitedAdmin] = useState('');
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [isDeassigning, setIsDeassigning] = useState(false);
  const [currentAssignments, setCurrentAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', data: null });
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // Data fetching
  const { data: usersData, isLoading, isError, error, refetch } = useFetchObjects('users', { 
    page, limit: rowPerPage, search, roleId: roleFilter, departmentId: departmentFilter, 
    isActive: isActiveFilter !== '' ? isActiveFilter : undefined
  }, user?.token);

  const { data: roles } = useFetchObjects('roles', {}, user?.token);
  const { data: departments } = useFetchObjects('departments', {}, user?.token);
  const { data: limitedAdmins, isLoading: loadingLimitedAdmins, error: limitedAdminsError } = useFetchObjects('limited-admin/limited-admins', {}, user?.token);
  const { data: allUsers, isLoading: loadingAllUsers, error: allUsersError } = useFetchObjects('users', { limit: 1000, page: 1 }, user?.token);
  const { handleDelete, ConfirmDialog } = useDelete('users', user?.token);

  // Effect to refetch data when pagination params change
  useEffect(() => {
    refetch();
  }, [page, rowPerPage]);

  // Debug limited admins data
  useEffect(() => {
    console.log('Limited Admins Data:', limitedAdmins);
    console.log('Loading Limited Admins:', loadingLimitedAdmins);
    console.log('Limited Admins Error:', limitedAdminsError);
  }, [limitedAdmins, loadingLimitedAdmins, limitedAdminsError]);

  // Handlers
  const handlePageChange = (_, value) => {
    console.log('Changing page to:', value);
    setPage(value);
  };
  const handleLimitChange = (e) => { setRowPerPage(parseInt(e.target.value)); setPage(1); };
  const handleSearchChange = (e) => setSearch(e.target.value);
  const handleSearch = (e) => { e.preventDefault(); setPage(1); refetch(); };
  const handleFilterChange = () => { setPage(1); refetch(); };
  const resetFilters = () => { 
    setSearch(''); setRoleFilter(''); setDepartmentFilter(''); setIsActiveFilter(''); refetch(); 
  };

  // Assignment handlers
  const handleAssignToLimitedAdmin = async (limitedAdminUser) => {
    setSelectedUser(limitedAdminUser);
    setSelectedLimitedAdmin('');
    setAssignmentNotes('');
    setCurrentAssignments([]);
    setAssignmentDialogOpen(true);
    
    // Fetch current assignments for this limited admin
    await fetchUserAssignments(limitedAdminUser.id);
  };

  const fetchUserAssignments = async (userId) => {
    setLoadingAssignments(true);
    try {
      const response = await fetch(`${API_BASE_URL}/limited-admin/user-assignments/${userId}`, {
        headers: {
          'Authorization': `Bearer ${user?.token}`
        }
      });
      
      const result = await response.json();
      if (result.success) {
        setCurrentAssignments(result.data || []);
      } else {
        console.error('Error fetching assignments:', result.message);
        setCurrentAssignments([]);
      }
    } catch (error) {
      console.error('Error fetching user assignments:', error);
      setCurrentAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleAssignmentSubmit = async () => {
    if (!selectedUser || !selectedLimitedAdmin) return;

    setIsAssigning(true);
    try {
      const response = await fetch(`${API_BASE_URL}/limited-admin/assign-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`
        },
        body: JSON.stringify({
          limitedAdminId: selectedUser.id, // The limited admin user
          userIds: [selectedLimitedAdmin], // The user to assign to the limited admin
          notes: assignmentNotes
        })
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message || 'User assigned to limited admin successfully',
          severity: 'success'
        });
        // Refresh assignments list
        await fetchUserAssignments(selectedUser.id);
        setSelectedLimitedAdmin('');
        setAssignmentNotes('');
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to assign user',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error assigning user:', error);
      setSnackbar({
        open: true,
        message: 'Error assigning user to limited admin',
        severity: 'error'
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const handleDeassignUser = (assignment) => {
    setConfirmDialog({
      open: true,
      type: 'deassign',
      data: assignment
    });
  };

  const handleConfirmDeassign = async () => {
    if (!confirmDialog.data) return;

    setIsDeassigning(true);
    try {
      const response = await fetch(`${API_BASE_URL}/limited-admin/remove-user-assignment`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`
        },
        body: JSON.stringify({
          limitedAdminId: confirmDialog.data.limited_admin_id,
          assignedUserId: confirmDialog.data.assigned_user_id
        })
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message || 'User deassigned successfully',
          severity: 'success'
        });
        // Refresh assignments list
        await fetchUserAssignments(selectedUser.id);
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to deassign user',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error deassigning user:', error);
      setSnackbar({
        open: true,
        message: 'Error deassigning user from limited admin',
        severity: 'error'
      });
    } finally {
      setIsDeassigning(false);
      setConfirmDialog({ open: false, type: '', data: null });
    }
  };

  const handleAssignmentCancel = () => {
    setAssignmentDialogOpen(false);
    setSelectedUser(null);
    setSelectedLimitedAdmin('');
    setAssignmentNotes('');
    setCurrentAssignments([]);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialog({ open: false, type: '', data: null });
  };

  // Reset password handlers
  const handleResetPassword = (user) => {
    setResetPasswordDialog({ open: true, user });
    setNewPassword('');
  };

  const handleResetPasswordSubmit = async () => {
    if (!newPassword.trim() || newPassword.length < 6) {
      setSnackbar({
        open: true,
        message: t('password_must_be_at_least_6_characters'),
        severity: 'error'
      });
      return;
    }

    setIsResettingPassword(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/${resetPasswordDialog.user.id}/reset-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`
        },
        body: JSON.stringify({
          newPassword: newPassword
        })
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({
          open: true,
          message: t('password_reset_successfully'),
          severity: 'success'
        });
        setResetPasswordDialog({ open: false, user: null });
        setNewPassword('');
      } else {
        setSnackbar({
          open: true,
          message: result.message || t('failed_to_reset_password'),
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setSnackbar({
        open: true,
        message: t('error_resetting_password'),
        severity: 'error'
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleResetPasswordCancel = () => {
    setResetPasswordDialog({ open: false, user: null });
    setNewPassword('');
  };

  // Check if user has permission to assign users to limited admins
  const canAssignToLimitedAdmin = () => {
    // Check if user has super admin role or limited admin management permissions
    return user?.role?.name === 'Super Admin' || 
           user?.role?.name === 'Admin' ||
           user?.permissions?.some(permission => 
             permission.code === 'manage_limited_admin_assignments' ||
             permission.code === 'super_admin_access'
           );
  };

  const canManageAssignments = (targetUser) => {
    // Only Limited Admin users can manage their own assignments
    return targetUser?.role?.name === 'Limited Admin';
  };

  // Data
  const usersList = usersData?.users || [];
  const totalPages = usersData?.totalPages || 1;
  const totalCount = usersData?.totalUsers || 0;

  // Filter available users for assignment (exclude already assigned users and limited admins)
  const getAvailableUsers = () => {
    // Handle different data structures from useFetchObjects
    const users = allUsers?.users || allUsers || [];
    if (!Array.isArray(users) || !currentAssignments) return [];
    
    const assignedUserIds = currentAssignments.map(assignment => 
      assignment.assignedUser?.id || assignment.limitedAdmin?.id
    );
    
    return users.filter(user => 
      user.is_active && 
      user.role?.name !== 'Limited Admin' && 
      user.role?.name !== 'Super Admin' &&
      !assignedUserIds.includes(user.id)
    );
  };

  const availableUsers = getAvailableUsers();

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'} sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          component={RouterLink}
          to="/users/create"
          sx={{ whiteSpace: 'nowrap', borderRadius: 2, px: 2, py: 0.75 }}
        >
          {t('create_new')}
        </Button>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          {t('list_title')}
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Box sx={{ display: 'flex', mb: 3, gap: 2, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
        <RTLTextField
          variant="outlined"
          size="small"
          placeholder={t('common:search')}
          value={search}
          onChange={handleSearchChange}
          {...getRTLProps('textfield')}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            dir: isRTL ? 'rtl' : 'ltr'
          }}
          sx={{ flexGrow: 1 }}
        />
        <Button variant="contained" onClick={handleSearch}>{t('common:search')}</Button>
        <Button variant="outlined" onClick={resetFilters}>{t('common:reset')}</Button>
        <Button variant="outlined" startIcon={<Refresh />} onClick={refetch}>{t('common:refresh')}</Button>
        <Button variant="text" onClick={() => setShowFilters(!showFilters)} startIcon={showFilters ? <ExpandLess /> : <ExpandMore />}>
          <FilterList fontSize="small" sx={{ mr: 0.5 }} /> {t('common:filter')}
        </Button>
      </Box>

      <Collapse in={showFilters}>
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <FormControl sx={{ minWidth: 200, flexGrow: 1 }} size="small">
              <InputLabel>{t('role')}</InputLabel>
              <Select
                value={roleFilter}
                onChange={(e) => { setRoleFilter(e.target.value); handleFilterChange(); }}
                label={t('role')}
              >
                <MenuItem value="">{t('common:all')}</MenuItem>
                {roles?.data?.map(role => <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 200, flexGrow: 1 }} size="small">
              <InputLabel>{t('department')}</InputLabel>
              <Select
                value={departmentFilter}
                onChange={(e) => { setDepartmentFilter(e.target.value); handleFilterChange(); }}
                label={t('department')}
              >
                <MenuItem value="">{t('common:all')}</MenuItem>
                {departments?.data?.map(dept => <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 200, flexGrow: 1 }} size="small">
              <InputLabel>{t('status')}</InputLabel>
              <Select
                value={isActiveFilter}
                onChange={(e) => { setIsActiveFilter(e.target.value); handleFilterChange(); }}
                label={t('status')}
              >
                <MenuItem value="">{t('common:all')}</MenuItem>
                <MenuItem value="true">{t('active_status')}</MenuItem>
                <MenuItem value="false">{t('inactive')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>
      </Collapse>

      {/* Error */}
      {isError && <Alert severity="error" sx={{ mb: 3 }}>{error?.message || t('common:error_fetching_data')}</Alert>}

      {/* Table */}
      <Paper sx={{ width: '100%', mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.100' }}>
              <TableRow>
                <RTLTableCell isrtl={isRTL ? 'true' : 'false'} align={isRTL ? "right" : "left"} sx={{ fontWeight: 600 }}>{t('id')}</RTLTableCell>
                <RTLTableCell isrtl={isRTL ? 'true' : 'false'} sx={{ fontWeight: 600 }}>{t('username')}</RTLTableCell>
                <RTLTableCell isrtl={isRTL ? 'true' : 'false'} sx={{ fontWeight: 600 }}>{t('email')}</RTLTableCell>
                <RTLTableCell isrtl={isRTL ? 'true' : 'false'} sx={{ fontWeight: 600 }}>{t('position')}</RTLTableCell>
                <RTLTableCell isrtl={isRTL ? 'true' : 'false'} sx={{ fontWeight: 600 }}>{t('role')}</RTLTableCell>
                <RTLTableCell isrtl={isRTL ? 'true' : 'false'} sx={{ fontWeight: 600 }}>{t('department')}</RTLTableCell>
                <RTLTableCell isrtl={isRTL ? 'true' : 'false'} sx={{ fontWeight: 600 }}>{t('status')}</RTLTableCell>
                <RTLTableCell isrtl={isRTL ? 'true' : 'false'} align="right" sx={{ fontWeight: 600 }}>{t('common:actions')}</RTLTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow><RTLTableCell colSpan={8} align="center" sx={{ py: 3 }} isrtl={isRTL ? 'true' : 'false'}><CircularProgress /></RTLTableCell></TableRow>
              ) : usersList.length === 0 ? (
                <TableRow><RTLTableCell colSpan={8} align="center" sx={{ py: 3 }} isrtl={isRTL ? 'true' : 'false'}>{t('common:no_records')}</RTLTableCell></TableRow>
              ) : (
                usersList.map((user, index) => (
                  <TableRow key={user.id} hover sx={{ '& td': { py: 1.5 } }}>
                    <RTLTableCell isrtl={isRTL ? 'true' : 'false'} align={isRTL ? "right" : "center"}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{(page - 1) * rowPerPage + index + 1}</Typography>
                    </RTLTableCell>
                    <RTLTableCell isrtl={isRTL ? 'true' : 'false'} sx={{ fontWeight: 500 }}>{user.username}</RTLTableCell>
                    <RTLTableCell isrtl={isRTL ? 'true' : 'false'}>{user.email || '-'}</RTLTableCell>
                    <RTLTableCell isrtl={isRTL ? 'true' : 'false'}>{user.position || '-'}</RTLTableCell>
                    <RTLTableCell isrtl={isRTL ? 'true' : 'false'}>{user.role?.name || '-'}</RTLTableCell>
                    <RTLTableCell isrtl={isRTL ? 'true' : 'false'}>{user.department?.name || '-'}</RTLTableCell>
                    <RTLTableCell isrtl={isRTL ? 'true' : 'false'}>
                      <Chip label={user.is_active ? t('active_status') : t('inactive')} color={user.is_active ? 'success' : 'error'} size="small" />
                    </RTLTableCell>
                    <RTLTableCell isrtl={isRTL ? 'true' : 'false'} align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
                        <Tooltip title={t('common:view')}>
                          <IconButton component={RouterLink} to={`/users/${user.id}`} size="small"><Visibility fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={t('common:edit')}>
                          <IconButton component={RouterLink} to={`/users/${user.id}/edit`} size="small"><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={t('reset_password')}>
                          <IconButton onClick={() => handleResetPassword(user)} size="small" color="warning"><LockReset fontSize="small" /></IconButton>
                        </Tooltip>
                        {canManageAssignments(user) && (
                          <Tooltip title={t('manage_assignments')}>
                            <IconButton onClick={() => handleAssignToLimitedAdmin(user)} size="small"><Assignment fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={user.is_active ? t('common:deactivate') : t('common:activate')}>
                          <IconButton onClick={() => handleDelete(user.id)} size="small"><Delete fontSize="small" /></IconButton>
                        </Tooltip>
                      </Box>
                    </RTLTableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{t('common:rows_per_page')}:</Typography>
            <Select value={rowPerPage} onChange={handleLimitChange} size="small" sx={{ minWidth: 70 }}>
              {[5, 10, 25, 50].map(num => <MenuItem key={num} value={num}>{num}</MenuItem>)}
            </Select>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              {t('common:showing')} {(page - 1) * rowPerPage + 1}-{Math.min(page * rowPerPage, totalCount)} {t('common:of')} {totalCount}
            </Typography>
            <Pagination
              count={Math.max(totalPages, 1)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              dir={isRTL ? 'rtl' : 'ltr'}
              shape="rounded"
              size="small"
              showFirstButton
              showLastButton
            />
          </Box>
        </Box>
      </Paper>
      
      {/* Comprehensive Assignment Management Dialog */}
      <Dialog 
        open={assignmentDialogOpen} 
        onClose={handleAssignmentCancel} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
            minHeight: '600px'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          textAlign: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <Assignment sx={{ fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {t('manage_limited_admin_assignments')}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {/* User Info Header */}
          <Box sx={{ p: 3, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                {selectedUser?.username?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedUser?.full_name || selectedUser?.username}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedUser?.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('role')}: {selectedUser?.role?.name || '-'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ p: 3 }}>
            {/* Current Assignments Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentTurnedIn color="primary" />
                {t('current_assignments')}
                <Badge badgeContent={currentAssignments.length} color="primary" />
              </Typography>
              
              {loadingAssignments ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : currentAssignments.length > 0 ? (
                <Card variant="outlined">
                  <List sx={{ p: 0 }}>
                    {currentAssignments.map((assignment, index) => {
                      // Handle both cases: limited admin viewing assigned users, or regular user viewing assigned limited admins
                      const displayUser = assignment.assignedUser || assignment.limitedAdmin;
                      
                      return (
                        <React.Fragment key={assignment.id}>
                          <ListItem sx={{ py: 2 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {displayUser?.full_name || displayUser?.username}
                                  </Typography>
                                  <Chip 
                                    label={t('active')} 
                                    size="small" 
                                    color="success" 
                                    icon={<CheckCircle />}
                                  />
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    {displayUser?.email}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {t('assigned_by')}: {assignment.assignedBy?.full_name || assignment.assignedBy?.username || t('system')}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {t('assigned_at')}: {new Date(assignment.assigned_at).toLocaleDateString()}
                                  </Typography>
                                  {assignment.notes && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                      "{assignment.notes}"
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Tooltip title={t('remove_assignment')}>
                                <IconButton 
                                  edge="end" 
                                  onClick={() => handleDeassignUser(assignment)}
                                  color="error"
                                  disabled={isDeassigning}
                                >
                                  <PersonRemove />
                                </IconButton>
                              </Tooltip>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < currentAssignments.length - 1 && <Divider />}
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Card>
              ) : (
                <Card variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('no_current_assignments')}
                  </Typography>
                </Card>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* New Assignment Section */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAdd color="primary" />
                {t('assign_to_limited_admin')}
              </Typography>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                  {loadingAllUsers 
                    ? t('loading') 
                    : t('users_available_for_assignment', '{{count}} users available for assignment', { count: availableUsers.length })
                  }
                </Typography>
              </Box>
              
              <RTLAutocomplete
                fullWidth
                {...getRTLProps('autocomplete')}
                options={availableUsers}
                getOptionLabel={(option) => option.full_name || option.username || t('common:unnamed_user', 'Unnamed user')}
                value={availableUsers.find(user => user.id === selectedLimitedAdmin) || null}
                onChange={(event, newValue) => setSelectedLimitedAdmin(newValue?.id || '')}
                loading={loadingAllUsers}
                disabled={loadingAllUsers}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(option => {
                    const fullName = (option.full_name || '').toLowerCase();
                    const username = (option.username || '').toLowerCase();
                    const email = (option.email || '').toLowerCase();
                    const department = (option.department?.name || '').toLowerCase();
                    const role = (option.role?.name || '').toLowerCase();
                    
                    return fullName.includes(searchTerm) || 
                           username.includes(searchTerm) || 
                           email.includes(searchTerm) ||
                           department.includes(searchTerm) ||
                           role.includes(searchTerm);
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('select_user_to_assign')}
                    placeholder={t('search_users_for_assignment', 'Search by name, username, email, department, or role...')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAllUsers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ p: 2, borderBottom: '1px solid #f0f0f0', '&:hover': { bgcolor: '#f8f9fa' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          bgcolor: 'primary.main', 
                          flexShrink: 0,
                          fontSize: '0.875rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {(option.full_name || option.username || 'U').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flexGrow: 1, textAlign: isRTL ? 'right' : 'left', minWidth: 0 }}>
                        <Typography variant="body1" fontWeight="600" sx={{ mb: 0.5 }} noWrap>
                          {option.full_name || option.username || t('common:unnamed_user', 'Unnamed user')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {option.email}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                          {option.role?.name && (
                            <Chip 
                              label={option.role.name} 
                              size="small" 
                              variant="outlined" 
                              color="primary"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                          {option.department?.name && (
                            <Chip 
                              label={option.department.name} 
                              size="small" 
                              variant="outlined" 
                              color="secondary"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
                noOptionsText={t('no_users_available_for_assignment', 'No users available for assignment')}
                loadingText={t('loading_users', 'Loading users...')}
                sx={{
                  '& .MuiAutocomplete-paper': {
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    border: '1px solid',
                    borderColor: 'divider',
                  },
                }}
              />
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('assignment_notes')}
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
                placeholder={t('assignment_notes_placeholder')}
                sx={{ mt: 2 }}
              />
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={handleAssignmentCancel} 
            disabled={isAssigning || isDeassigning}
            size="large"
          >
            {t('common:cancel')}
          </Button>
          <Button 
            onClick={handleAssignmentSubmit} 
            variant="contained" 
            disabled={!selectedLimitedAdmin || isAssigning || isDeassigning}
            startIcon={isAssigning ? <CircularProgress size={20} /> : <PersonAdd />}
            size="large"
            sx={{ minWidth: 140 }}
          >
            {isAssigning ? t('assigning') : t('assign')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleConfirmDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          {t('confirm_action')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.type === 'deassign' && (
              <>
                {t('confirm_deassign_user', { 
                  userName: selectedUser?.full_name || selectedUser?.username,
                  adminName: confirmDialog.data?.limitedAdmin?.full_name || confirmDialog.data?.limitedAdmin?.username
                })}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose} disabled={isDeassigning}>
            {t('common:cancel')}
          </Button>
          <Button 
            onClick={handleConfirmDeassign} 
            variant="contained" 
            color="error"
            disabled={isDeassigning}
            startIcon={isDeassigning ? <CircularProgress size={20} /> : <PersonRemove />}
          >
            {isDeassigning ? t('removing') : t('confirm_remove')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Reset Password Dialog */}
      <Dialog 
        open={resetPasswordDialog.open} 
        onClose={handleResetPasswordCancel} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <LockReset color="warning" />
          {t('reset_password')}
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          {resetPasswordDialog.user && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {t('reset_password_for_user', { 
                  userName: resetPasswordDialog.user.full_name || resetPasswordDialog.user.username 
                })}
              </Typography>
              
              <TextField
                fullWidth
                type="password"
                label={t('new_password')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('enter_new_password')}
                helperText={t('password_minimum_6_characters')}
                error={newPassword.length > 0 && newPassword.length < 6}
              />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={handleResetPasswordCancel} 
            disabled={isResettingPassword}
          >
            {t('common:cancel')}
          </Button>
          <Button 
            onClick={handleResetPasswordSubmit} 
            variant="contained" 
            color="warning"
            disabled={!newPassword.trim() || newPassword.length < 6 || isResettingPassword}
            startIcon={isResettingPassword ? <CircularProgress size={20} /> : <LockReset />}
          >
            {isResettingPassword ? t('resetting') : t('reset_password')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <ConfirmDialog />
    </Box>
  );
};

export default UserList;